import {
    AppAvatar,
    AppBox,
    AppButton,
    AppIconButton,
    AppLink,
    AppModal,
    AppPaper,
    AppText,
    AppTitle,
} from "../../../../commons/components";
import AppBorderBox from "../../../../commons/components/BorderBox";
import IconBin from "../../../../commons/components/icons/bin";
import IconEmpty from "../../../../commons/components/icons/empty";
import SkeletonGlobalCardList from "../../../../commons/components/skeleton/SkeletonGlobalCardList";
import {StatusEnum} from "../../../../commons/enums/status-enum";
import {useAppDispatch, useAppSelector} from "../../../../hooks/app";
import {useEffect, useState} from "react";
import {fetchManageStadiumList} from "../../../../redux/slices/stadium/manage-stadium-list-slice";
import {Stadium} from "../../../../commons/models/stadium";
import {stadiumDelete} from "../../../../redux/slices/stadium/manage-stadium-delete-slice";
import StadiumDelete from "./StadiumDelete";
import StadiumDeleteInprogress from "./StadiumDeleteInprogress";
import {useSnackbar} from "../../../../redux/snackbarProvider";
import setDocumentTitle from "../../../../commons/utilities/document-title";
import {AxiosError} from "axios";
import {IServerErrorResponse} from "../../../../commons/components/interface";
import StadiumAddEdit from "./stadiumAddEdit/StadiumAddEdit";
import IconEdit from "../../../../commons/components/icons/edit";
import {toTitleCase} from "../../../../commons/utilities/string-utils";
import {cleanUpStepperFormData} from "../../../../redux/slices/stepper-form-slice";
import IconStadium from "../../../../commons/components/icons/stadium";
import {Can} from "../../../../casl/Can";
import {RolePolicyActionEnum} from "../../../../commons/enums/role-policy-action.enum";
import useFilter from "../../../../hooks/useFilter";
import FilterToolbar from "../../../../commons/components/ui-components/FilterToolbar";

export default function StadiumList() {
    const dispatch = useAppDispatch();
    const addSnackbar = useSnackbar();
    const {stadiumList, stadiumListStatus} = useAppSelector((state) => state.manageStadiumList);
    const {stadiumDeleteStatus} = useAppSelector((state) => state.manageStadiumDelete);
    const [isDeleteStadium, setDeleteStadium] = useState(false);
    const [isAddEditStadium, setAddEditStadium] = useState(false);
    const [selectedStadium, setSelectedStadium] = useState<Stadium | null>(null);
    const {sortBy, sortOrder,searchText} = useFilter();
    const { stadiumAddResponseStatus } = useAppSelector((state) => state.manageStadiumAdd);
    const { stadiumEditResponseStatus } = useAppSelector((state) => state.manageStadiumEdit);

    const fetchStadiumList = () =>{
        dispatch(fetchManageStadiumList({sort: sortBy, sortOrder, name: searchText}));
    }
    const handleDeleteClick = (stadium: Stadium) => (event: any) => {
        setSelectedStadium(stadium);
        setDeleteStadium(true);
    };

    const handleEditClick = (stadium: Stadium) => (event: any) => {
        setSelectedStadium(stadium);
        setAddEditStadium(true);
    };

    const handleAfterAddEdit = () => {
        setSelectedStadium(null);
        setAddEditStadium(false);
        fetchStadiumList();
    };

    const handleAfterAddEditClose = () => {
        setSelectedStadium(null);
        setAddEditStadium(false);
        dispatch(cleanUpStepperFormData());
    };

    const handleDeleteConfirm = (e: Event) => {
        e.preventDefault();
        if (selectedStadium) {
            setDeleteStadium(false);
            dispatch(stadiumDelete(selectedStadium.id))
                .unwrap()
                .then(() => {
                    addSnackbar({
                        key: "stadium-delete-success",
                        text: "Venue Deleted Successfully",
                        variant: "success",
                    });
                    setSelectedStadium(null);
                    fetchStadiumList();
                })
                .catch((error: AxiosError<IServerErrorResponse>) => {
                    addSnackbar({
                        key: "error",
                        text: error.response?.data.message,
                        variant: "danger",
                    });
                });
        }
    };

    const handleClick = () => {
        window.scrollTo(0, 0);
    };

    useEffect(() => {
        setDocumentTitle(["Venues", "Manage"]);
    }, [stadiumList]);

    return (
        <>
            <AppBox flexDirection="column" gap="xs">
                <AppBox alignItems="center" justifyContent="space-between">
                    <AppTitle as="h4">All Venues</AppTitle>
                    <Can I={RolePolicyActionEnum.CREATE} a={Stadium}>
                        {stadiumList.length > 0 &&
                            <AppButton label="Add Venue" onClick={() => setAddEditStadium(true)}/>}
                    </Can>
                </AppBox>
                <FilterToolbar enableStatus={false}/>
                {stadiumListStatus === StatusEnum.Loading ? (
                    <SkeletonGlobalCardList noTitle numberOfItems={10} withAvatar withBadge/>
                ) : (
                    <>
                        {stadiumList.length === 0 ? (
                            <AppPaper style={{height: "100%"}}>
                                <AppBorderBox borderVariant="dashed">
                                    <AppBox
                                        flexDirection="column"
                                        alignItems="center"
                                        gap="sm"
                                        justifyContent="center"
                                        style={{height: "calc(100vh - 16rem)"}}
                                    >
                                        <AppBox alignItems="center" flexDirection="column" justifyContent="center"
                                                gap="2xs">
                                            <IconEmpty width={56} height={56} color="rgba(var(--border-200))"/>
                                            <AppTitle as="h6">No Venues Found</AppTitle>
                                            <Can I={RolePolicyActionEnum.CREATE} a={Stadium}>
                                                <AppText>Ready to kick things off? Let's get started by adding
                                                    venues.</AppText>
                                            </Can>
                                        </AppBox>
                                        <Can I={RolePolicyActionEnum.CREATE} a={Stadium}>
                                            <AppButton label="Add New Venue" onClick={() => setAddEditStadium(true)}/>
                                        </Can>
                                    </AppBox>
                                </AppBorderBox>
                            </AppPaper>
                        ) : (
                            <>
                                {stadiumList.mapArray((stadium, index) => (
                                    <AppPaper radius="sm" padding="none" shadow="xs" key={index}>
                                        <AppBox gap="xs" my="2xs" mx="xs" justifyContent="space-between">
                                            <AppLink to={`/manage/stadiums/${stadium.id}`} onClick={handleClick}>
                                                <AppBox gap="xs" alignItems="center">
                                                    <AppAvatar username={stadium.name} src={stadium.imageUrl}
                                                               size="lg"/>
                                                    <AppTitle as={"h5"}>{toTitleCase(stadium.name)}</AppTitle>
                                                </AppBox>
                                            </AppLink>
                                            <AppBox gap="xs" justifyContent="end" alignItems="center">
                                                <Can I={RolePolicyActionEnum.UPDATE} a={Stadium}>
                                                    <AppIconButton icon={<IconEdit/>} variant="light"
                                                                   onClick={handleEditClick(stadium)}/>
                                                </Can>
                                                <Can I={RolePolicyActionEnum.DELETE} a={Stadium}>
                                                    <AppIconButton
                                                        icon={<IconBin/>}
                                                        variant="light"
                                                        color="danger"
                                                        onClick={handleDeleteClick(stadium)}
                                                    />
                                                </Can>
                                            </AppBox>
                                        </AppBox>
                                    </AppPaper>
                                ))}
                            </>
                        )}
                    </>
                )}
            </AppBox>
            <AppModal
                opened={isAddEditStadium}
                modalSize="lg"
                onClose={handleAfterAddEditClose}
                title={selectedStadium ? "Edit Venue" : "Add New Venue"}
                titleIcon={selectedStadium ? <IconEdit/> : <IconStadium/>}
                withoutButtons
                withLoadingButton={
                    stadiumAddResponseStatus === StatusEnum.Loading || stadiumEditResponseStatus === StatusEnum.Loading
                }
            >
                <StadiumAddEdit stadiumData={selectedStadium} onSave={handleAfterAddEdit}
                                onCancel={handleAfterAddEditClose}/>
            </AppModal>
            {selectedStadium && (
                <>
                    <AppModal
                        opened={isDeleteStadium}
                        onClose={(e) => {
                            setSelectedStadium(null);
                            setDeleteStadium(false);
                        }}
                        title="Want To Delete This Venue?"
                        titleIcon={<IconBin/>}
                        iconColor="danger"
                        confirmButtonLabel="Delete"
                        onSave={(e) => {
                            handleDeleteConfirm(e as Event);
                        }}
                    >
                        <StadiumDelete stadium={selectedStadium}/>
                    </AppModal>
                    <AppModal
                        opened={stadiumDeleteStatus === StatusEnum.Loading}
                        title="Deleting Venue"
                        titleIcon={<IconBin/>}
                        iconColor="danger"
                        withoutCancelButton
                        withoutButtons
                        withoutCloseButton
                    >
                        <StadiumDeleteInprogress stadium={selectedStadium}/>
                    </AppModal>
                </>
            )}
        </>
    );
}
